import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Undo, Printer, Loader2, Info, Pencil, FileText, ChevronLeft, Package, Store, Receipt, Calendar, MessageSquare, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import { DataTable2 } from '../components/ui/DataTable2';
import Accordion from '../components/ui/Accordion';
import FastAPIClient from '../services/apiClient';
import { useToast } from '../hooks/useToast';
import { fetchPurchaseOrdersForVendor, goodsReceiptDocument } from '../apiHelper';
import PurchaseOrderPrintView from './PurchaseOrderPrintView';

const client = new FastAPIClient();

/**
 * VendorPurchaseOrders Component.
 * Displays a list of purchase orders for a specific vendor.
 * Receives vendor_id and vendor_name via location state.
 */
const VendorPurchaseOrders = () => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const location = useLocation();
    const vendorId = location.state?.vendor_id;
    const vendorName = location.state?.vendor_name || 'Selected Vendor';

    const [user, setUser] = useState(null);
    const [purchaseOrders, setPurchaseOrders] = useState([]);
    const [loadingPOs, setLoadingPOs] = useState(true);
    const [errorPOs, setErrorPOs] = useState(null);
    const [selectedPoForCheckboxDetails, setSelectedPoForCheckboxDetails] = useState(null);
    const [showPrintView, setShowPrintView] = useState(false);

    // Current date and time for past-due checks
    const currentDate = new Date(); // WAT is UTC+1

    // Effect to load user from local storage
    useEffect(() => {
        const lcUser = localStorage.getItem("user");
        if (!lcUser) {
            showToast("Please log in to view purchase orders.", "info");
            navigate('/login');
        } else {
            setUser(JSON.parse(lcUser));
        }
    }, [showToast, navigate]);

    // Fetch purchase orders for the selected vendor
    useEffect(() => {
        const loadPurchaseOrders = async () => {
            if (!user) return;
            if (!vendorId) {
                setLoadingPOs(false);
                return;
            }
            setLoadingPOs(true);
            setErrorPOs(null);
            try {
                const fetchedPOs = await fetchPurchaseOrdersForVendor(vendorId);
                // console.log(fetchedPOs);
                setPurchaseOrders(fetchedPOs);
            } catch (err) {
                console.error("Error loading purchase orders:", err);
                const errorMessage = err.response?.data?.detail || `Failed to load purchase orders: ${err.message || err}`;
                setErrorPOs(errorMessage);
                showToast(errorMessage, "error");
            } finally {
                setLoadingPOs(false);
            }
        };

        loadPurchaseOrders();
    }, [user, vendorId, showToast]);

    // Function to handle checkbox selection for detailed view
    const handleCheckboxSelect = useCallback((po) => {
        setSelectedPoForCheckboxDetails(prevSelected =>
            prevSelected && prevSelected.purchaseorder_id === po.purchaseorder_id ? null : po
        );
    }, []);

    // Function to check if a PO has any past-due items
    const isPastDue = useCallback((po) => {
        if (!po.poitems || po.poitems.length === 0) return false;
        return po.poitems.some(item => {
            if (!item.expecteddate) return false;
            const expectedDate = new Date(item.expecteddate);
            return expectedDate < currentDate;
        });
    }, [currentDate]);

    const receiptRowActions = useMemo(() => [
        { label: 'Edit', key: 'edit_goodsreceipt', icon: <Pencil size={14} /> },
        { label: 'Approve', key: 'approve_receipt', icon: <CheckCircle size={14} /> },
        { label: 'Reject', key: 'reject_receipt', icon: <XCircle size={14} /> },
    ], []);


    const renderExpandedPOItems = useCallback((po) => {
        if (!po.poitems || po.poitems.length === 0) {
            return <div className="p-4 text-gray-600 bg-gray-50 rounded-b-lg">No items found.</div>;
        }

        // UPDATED: Added event parameter to prevent bubbling
        const handleAction = (e, key, item) => {
            e.preventDefault();
            e.stopPropagation();

            const restrictedActions = ['edit_goodsreceipt', 'approve_receipt', 'reject_receipt'];

            if (restrictedActions.includes(key)) {
                const qtyReceived = item.goodsreceiptitem?.qtyreceived || 0;

                if (qtyReceived === 0) {
                    showToast(`Action restricted: Goods records for items not yet received cannot be processed.`, "error");
                    return; // Block execution
                }
            }

            showToast(`Action ${key} triggered for ${item.catentry?.productname}`, "info");
            if (key === 'edit_goodsreceipt') {

                const sendingState = {
                    vendor_id: vendorId,
                    vendor_name: vendorName,
                    purchaseorder_id: po.purchaseorder_id,
                    ponumber: po.ponumber,
                    currency: po.store?.setccurr || 'NGN',
                    // Pass all items belonging to this PO
                    items: po.poitems.map(pi => ({
                        catentry_id: pi.catentry_id,
                        productname: pi.catentry?.productname,
                        partnumber: pi.catentry?.partnumber,
                        cost: pi.goodsreceiptitem?.cost,
                        listprice: pi.goodsreceiptitem?.listprice,
                        qtyordered: pi.qtyordered,
                        qtyreceived: pi.goodsreceiptitem?.qtyreceived || 0,
                        goodsreceipt_id: pi.goodsreceiptitem?.goodsreceipt_id
                    }))
                };

                navigate('/vendor/edit-vendor-goods-receipt', {
                    state: sendingState
                })
            } else if (key === "approve_receipt") {
                const payload = {
                    goodsreceipt_id: item.goodsreceiptitem.goodsreceipt_id,
                    purchaseorder_id: item.purchaseorder_id,
                }
                navigate('/vendor/approve-vendor-goods-receipt', {
                    state: payload
                })
            } else if (key === "reject_receipt") {
                const payload = {
                    goodsreceipt_id: item.goodsreceiptitem.goodsreceipt_id,
                    purchaseorder_id: item.purchaseorder_id,
                }
            }
        };

        return (
            <div className="p-2 sm:p-4 bg-gray-50 rounded-b-lg">
                <h4 className="font-bold text-lg mb-3 text-text-primary px-2 border-b pb-2">
                    Purchase Order Items
                </h4>

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full text-sm text-text-primary bg-white rounded-lg shadow-inner">
                        <thead className="bg-gray-200">
                            <tr>
                                <th className="px-4 py-2 text-left font-semibold">Product</th>
                                <th className="px-4 py-2 text-left font-semibold">Cost</th>
                                <th className="px-4 py-2 text-left font-semibold">List Price</th>
                                <th className="px-4 py-2 text-left font-semibold">Ordered</th>
                                <th className="px-4 py-2 text-left font-semibold">Received</th>
                                <th className="px-4 py-2 text-left font-semibold">Goods Receipt</th>
                                <th className="px-4 py-2 text-left font-semibold">Remaining</th>
                                <th className="px-4 py-2 text-center font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {po.poitems.map((item, idx) => (
                                <tr key={`${item.podetail_id}-${idx}`} className="border-b hover:bg-gray-50 transition-colors">
                                    <td className="px-4 py-3 font-medium">{item.catentry?.productname}</td>
                                    <td className="px-4 py-3 text-blue-600 font-medium">
                                        {item.catentry?.listprice?.currency_rel?.setcnote}
                                        {(item?.goodsreceiptitem?.cost || 0).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3 text-gray-500">
                                        {item.catentry?.listprice?.currency_rel?.setcnote}
                                        {(item.catentry?.listprice?.listprice || 0).toFixed(2)}
                                    </td>
                                    <td className="px-4 py-3">{item.qtyordered}</td>
                                    <td className="px-4 py-3 font-semibold">{item.qtyreceived}</td>
                                    <td className="px-4 py-3 font-semibold">{item.goodsreceiptitem?.qtyreceived}</td>
                                    <td className="px-4 py-3 text-orange-600">{item.qtyremaining}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex justify-center gap-2">
                                            {receiptRowActions.map(action => (
                                                <button
                                                    key={action.key}
                                                    // CRITICAL: Pass event to handleAction
                                                    onClick={(e) => handleAction(e, action.key, item)}
                                                    className="p-2 hover:bg-blue-50 rounded-full text-blue-600 transition-colors border border-transparent hover:border-blue-200"
                                                    title={action.label}
                                                >
                                                    {action.icon}
                                                </button>
                                            ))}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Card View (Updated for consistency) */}
                <div className="md:hidden space-y-3">
                    {po.poitems.map((item, idx) => (
                        <div key={idx} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                            <div className="flex justify-between items-start mb-3">
                                <div className="flex flex-col">
                                    <span className="font-bold text-text-primary">{item.catentry?.productname}</span>
                                    <span className="text-xs text-gray-500">{item.catentry?.partnumber}</span>
                                </div>
                                <span className="text-xs font-bold text-blue-600">
                                    Cost: {item.catentry?.listprice?.currency_rel?.setcnote}{(item?.goodsreceiptitem?.cost || 0).toFixed(2)}
                                </span>
                            </div>

                            <div className="grid grid-cols-3 gap-2 text-center text-xs bg-gray-50 p-2 rounded mb-4">
                                <div><p className="text-gray-400 uppercase text-[10px]">Ordered</p><p className="font-bold">{item.qtyordered}</p></div>
                                <div><p className="text-gray-400 uppercase text-[10px]">Received</p><p className="font-bold">{item.qtyreceived}</p></div>
                                <div><p className="text-gray-400 uppercase text-[10px]">Remaining</p><p className="font-bold text-orange-600">{item.qtyremaining}</p></div>
                            </div>

                            <div className="flex gap-2 pt-1">
                                {receiptRowActions.map(action => (
                                    <button
                                        key={action.key}
                                        onClick={(e) => handleAction(e, action.key, item)}
                                        className="flex-1 flex flex-col items-center justify-center gap-1 py-2 text-[10px] font-semibold border rounded-md hover:bg-blue-50 text-blue-700"
                                    >
                                        {action.icon}
                                        {action.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }, [receiptRowActions, showToast]);

    // Columns for the Purchase Orders DataTable
    const poColumns = useMemo(() => [
        {
            key: 'select',
            label: 'Details',
            render: (value, row) => (
                <input
                    type="checkbox"
                    className="form-checkbox h-4 w-4 text-blue-600 transition duration-150 ease-in-out rounded focus:ring-blue-500"
                    checked={selectedPoForCheckboxDetails?.purchaseorder_id === row.purchaseorder_id}
                    onChange={() => handleCheckboxSelect(row)}
                />
            )
        },
        {
            key: 'ponumber',
            label: 'PO Number',
            render: (ponumber) => (
                <div className="flex items-center gap-2">
                    <FileText size={16} className="text-blue-500" />
                    <span>{ponumber}</span>
                </div>
            )
        },
        {
            key: 'store.identifier',
            label: 'Store',
            render: (value, row) => (
                <div className="flex items-center gap-2">
                    <Store size={16} className="text-purple-500" />
                    <span>{row.store?.identifier || 'N/A'}</span>
                </div>
            )
        },
        {
            key: 'goodsreceiptStatus',
            label: 'GoodsReceipt Approval Status',
            render: (value, row) => {
                // Get status from the first item's goodsreceiptitem object
                const status = row.poitems?.[0]?.goodsreceiptitem?.status || 'N/A';

                const statusStyles = {
                    'PENDING': 'bg-yellow-100 text-yellow-800',
                    'VERIFIED': 'bg-blue-100 text-blue-800',
                    'REJECTED': 'bg-red-100 text-red-800',
                    'N/A': 'bg-gray-100 text-gray-800'
                };

                return (
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${statusStyles[status] || statusStyles['N/A']}`}>
                        {status}
                    </span>
                );
            }
        },
        {
            key: 'orderdate',
            label: 'Order Date',
            render: (orderdate) => {
                try {
                    return new Date(orderdate).toLocaleDateString(undefined, {
                        year: 'numeric', month: 'short', day: 'numeric'
                    });
                } catch {
                    return 'Invalid Date';
                }
            }
        },
        {
            key: 'openindicator',
            label: 'Status',
            render: (openindicator) => (
                <span className={`px-2 py-1 rounded-full text-xs font-semibold ${openindicator === 'Y' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {openindicator === 'Y' ? 'Open' : 'Closed'}
                </span>
            )
        },
        {
            key: 'itemSummary',
            label: 'Items Summary',
            render: (value, row) => {
                const itemCount = row.poitems?.length || 0;
                return (
                    <span className="text-sm font-medium text-gray-700">
                        {itemCount} {itemCount === 1 ? 'item' : 'items'}
                    </span>
                );
            }
        },
        {
            key: 'pastDue',
            label: 'Past Due',
            render: (value, row) => (
                <div className="flex items-center gap-2">
                    {isPastDue(row) ? (
                        <>
                            <AlertTriangle size={16} className="text-red-600" />
                            <span className="text-red-600">Yes</span>
                        </>
                    ) : (
                        <span>No</span>
                    )}
                </div>
            )
        }
    ], [selectedPoForCheckboxDetails, handleCheckboxSelect, isPastDue]);

    if (!user) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-orange-600'>
                <Info size={32} className="mb-2" />
                <p className="text-lg mb-4">Please log in to view purchase orders.</p>
            </div>
        );
    }

    if (!vendorId) {
        return (
            <div className='flex flex-col items-center justify-center min-h-[60vh] text-center p-8 text-red-600'>
                <XCircle size={32} className="mb-2" />
                <p className="text-lg mb-4">No vendor selected. Please go back to Vendor Management to view their purchase orders.</p>
                <Button onClick={() => navigate('/vendor')} variant="primary" className="mt-4">
                    <ChevronLeft size={20} className="mr-2" /> Go to Vendor Management
                </Button>
            </div>
        );
    }

    return (
        <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
            <Card className="p-4 sm:p-6 shadow-lg rounded-lg">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-2xl font-bold text-text-primary flex items-center gap-2">
                        <FileText size={24} className="text-blue-600" /> Purchase Orders for {vendorName}
                    </h2>
                    <div className="flex gap-2 flex-wrap">
                        <Button onClick={() => navigate('/vendor')} variant="secondary" size="sm" className="flex items-center justify-center px-4 py-3">
                            <ChevronLeft size={20} className="mr-2" /> Back to Vendors
                        </Button>
                        <Button
                            onClick={() => setShowPrintView(true)}
                            variant="secondary"
                            size="sm"
                            className="flex items-center gap-1"
                            disabled={!selectedPoForCheckboxDetails}
                            title={!selectedPoForCheckboxDetails ? 'Select a purchase order checkbox to print.' : 'Print Purchase Order PDF'}
                        >
                            <Printer size={18} /> Print PO PDF
                        </Button>
                        <Button
                            onClick={() => navigate('/vendor/vendor-goods-receipt', {
                                state: {
                                    vendor_id: vendorId,
                                    vendor_name: vendorName,
                                    ponumber: selectedPoForCheckboxDetails?.ponumber
                                }
                            })}
                            variant="primary"
                            size="sm"
                            className="flex items-center gap-1"
                            disabled={!selectedPoForCheckboxDetails || selectedPoForCheckboxDetails.openindicator !== 'Y'}
                            title={selectedPoForCheckboxDetails?.openindicator !== 'Y' ? 'Cannot record goods for a closed purchase order.' : ''}
                        >
                            <Receipt size={18} /> Record Goods Receipt
                        </Button>
                        <Button
                            onClick={() => navigate('/vendor/return-to-vendor', {
                                state: {
                                    vendor_id: vendorId,
                                    vendor_name: vendorName,
                                    poNumber: selectedPoForCheckboxDetails?.ponumber,
                                    po_id: selectedPoForCheckboxDetails?.purchaseorder_id
                                }
                            })}
                            variant="secondary"
                            size="sm"
                            className="flex items-center gap-1"
                            disabled={!selectedPoForCheckboxDetails} // Returns often happen on closed POs too
                        >
                            <Undo size={18} /> Record Return to Vendor
                        </Button>
                    </div>
                </div>
                <p className="text-text-secondary mb-6">
                    Here you can view all <span className="text-gmail-blue text-bold">OPEN</span> purchase orders associated with {vendorName}. <br /> Expand a row or use the checkbox to see item details.
                </p>

                {loadingPOs ? (
                    <div className="flex items-center justify-center py-8">
                        <Loader2 className="animate-spin h-6 w-6 mr-2 text-blue-500" />
                        <p className="text-gray-600">Loading purchase orders...</p>
                    </div>
                ) : errorPOs ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md mb-4" role="alert">
                        <p>{errorPOs}</p>
                    </div>
                ) : (
                    <DataTable2
                        columns={poColumns}
                        data={purchaseOrders}
                        loading={loadingPOs}
                        error={errorPOs}
                        emptyMessage={`No purchase orders found for ${vendorName}.`}
                        enableSearch={true}
                        enablePagination={true}
                        rowsPerPage={10}
                        enableRowExpansion={true}
                        renderExpandedContent={renderExpandedPOItems}
                    />
                )}
            </Card>

            {/* Accordion for detailed PO view when checkbox is selected */}
            {selectedPoForCheckboxDetails && (
                <Card className="mt-6 p-4 sm:p-6 shadow-lg rounded-lg">
                    <Accordion title={`Details for PO: ${selectedPoForCheckboxDetails.ponumber}`} defaultOpen={true}>
                        {renderExpandedPOItems(selectedPoForCheckboxDetails)}
                    </Accordion>
                </Card>
            )}
            {showPrintView && selectedPoForCheckboxDetails && (
                <PurchaseOrderPrintView
                    purchaseOrder={selectedPoForCheckboxDetails}
                    vendorName={vendorName}
                    onClose={() => setShowPrintView(false)}
                />
            )}
        </div>
    );
};

export default VendorPurchaseOrders;